import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Globe, BookOpen, Award, Save, Loader2 } from 'lucide-react'
import { apiClient } from '../../lib/api'

const INTERFACE_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español (Spanish)' },
  { code: 'uk', name: 'Українська (Ukrainian)' },
  { code: 'de', name: 'Deutsch (German)' },
  { code: 'nb', name: 'Norsk (Norwegian)' },
]

const STUDY_LANGUAGES = [
  'English',
  'Spanish',
  'French',
  'German',
  'Russian',
  'Italian',
  'Portuguese',
  'Dutch',
  'Polish',
  'Norwegian',
  'Swedish',
  'Danish',
  'Finnish',
  'Czech',
  'Japanese',
  'Chinese',
  'Korean',
  'Arabic',
  'Turkish',
]

const CEFR_LEVELS = [
  { code: 'A1', name: 'A1 - Beginner', description: 'Can understand basic phrases and expressions' },
  { code: 'A2', name: 'A2 - Elementary', description: 'Can communicate in simple, routine tasks' },
  { code: 'B1', name: 'B1 - Intermediate', description: 'Can deal with most situations while traveling' },
  { code: 'B2', name: 'B2 - Upper Intermediate', description: 'Can interact with native speakers fluently' },
  { code: 'C1', name: 'C1 - Advanced', description: 'Can use language flexibly and effectively' },
  { code: 'C2', name: 'C2 - Proficient', description: 'Can understand virtually everything' },
]

export function LanguagePreferences() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  
  const [interfaceLanguage, setInterfaceLanguage] = useState('en')
  const [studyLanguage, setStudyLanguage] = useState('')
  const [cefrLevel, setCefrLevel] = useState('')

  useEffect(() => {
    loadPreferences()
  }, [])

  const loadPreferences = async () => {
    try {
      setLoading(true)
      const data = await apiClient.getUserPreferences()
      
      if (data.preferences) {
        setInterfaceLanguage(data.preferences.preferred_interface_language || 'en')
        setStudyLanguage(data.preferences.preferred_study_language || '')
        setCefrLevel(data.preferences.preferred_cefr_level || '')
      }
    } catch (error) {
      console.error('Failed to load preferences:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setSaved(false)
      
      await apiClient.updateUserPreferences({
        preferred_interface_language: interfaceLanguage,
        preferred_study_language: studyLanguage || undefined,
        preferred_cefr_level: cefrLevel || undefined,
      })
      
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (error) {
      console.error('Failed to save preferences:', error)
      alert('Failed to save preferences. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Interface Language */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Interface Language
          </CardTitle>
          <CardDescription>
            Choose the language for the application interface. This setting will be used for menus, buttons, and other UI elements.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Label htmlFor="interface-language">Select Language</Label>
            <select
              id="interface-language"
              value={interfaceLanguage}
              onChange={(e) => setInterfaceLanguage(e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {INTERFACE_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
            <p className="text-sm text-muted-foreground">
              Note: Currently only English is fully supported. Other languages coming soon.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Study Language */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Language You're Learning
          </CardTitle>
          <CardDescription>
            Select the language you're studying. This will be used to filter library books and show relevant content.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Label htmlFor="study-language">Select Language</Label>
            <select
              id="study-language"
              value={studyLanguage}
              onChange={(e) => setStudyLanguage(e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- Select a language --</option>
              {STUDY_LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
            {studyLanguage && (
              <p className="text-sm text-green-600 dark:text-green-500">
                Library will prioritize showing books available in {studyLanguage}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* CEFR Level */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Your Current Level (CEFR)
          </CardTitle>
          <CardDescription>
            Select your current proficiency level based on the Common European Framework of Reference for Languages (CEFR).
            This helps us recommend appropriate reading materials.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Label htmlFor="cefr-level">Select Level</Label>
            <select
              id="cefr-level"
              value={cefrLevel}
              onChange={(e) => setCefrLevel(e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- Select your level --</option>
              {CEFR_LEVELS.map((level) => (
                <option key={level.code} value={level.code}>
                  {level.name}
                </option>
              ))}
            </select>
            
            {cefrLevel && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium text-foreground mb-1">
                  {CEFR_LEVELS.find(l => l.code === cefrLevel)?.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {CEFR_LEVELS.find(l => l.code === cefrLevel)?.description}
                </p>
              </div>
            )}

            {/* CEFR Level Reference */}
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <h4 className="text-sm font-semibold text-foreground mb-3">CEFR Level Reference:</h4>
              <div className="space-y-2">
                {CEFR_LEVELS.map((level) => (
                  <div key={level.code} className="flex items-start gap-3">
                    <span className="text-sm font-medium text-foreground min-w-[3rem]">
                      {level.code}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {level.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex items-center justify-between pt-4">
        <div>
          {saved && (
            <p className="text-sm text-green-600 dark:text-green-500 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-600 rounded-full"></span>
              Preferences saved successfully
            </p>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Preferences
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

